import { translate } from "app/filters/i18nFilter";
import type { TrafficSignalEventDto } from "app/pages/sales-intelligence/sub-modules/account-overview/company/types/signals";
import { PERIOD_TO_NAME_MAP } from "app/pages/sales-intelligence/sub-modules/signals/constants/common";
import type TrafficChangesModel from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficChangesModel";
import type TrafficMarketModel from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficMarketModel";
import { isTrafficChangesModel } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficChangesModel";
import { isTrafficMarketModel } from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/type-guards/isTrafficMarketModel";
import trafficSignalFiltersService from "app/pages/sales-intelligence/sub-modules/signals/services/ui-signals/traffic/trafficSignalFiltersService";
import CountryService from "app/services/CountryService";

function buildPeriodText<F extends { readonly period: string }>(filters: F): string {
    const { period } = filters;

    return `over the last ${PERIOD_TO_NAME_MAP[period]}`;
}

function buildTrafficMetricText<F extends { readonly metric: string }>(filters: F): string {
    const { metric } = filters;

    return translate(`si.signals.filters.traffic-metric.${metric}.name`);
}

function buildCountryText<F extends { readonly country: number }>(filters: F): string {
    const { country } = filters;

    const { text = "" } = CountryService.getCountryById(country) ?? {};

    return text;
}

function buildCountriesText(countries: readonly number[]): string {
    const numberOfCountries = countries.length;

    if (numberOfCountries === 1) {
        return `${numberOfCountries} country`;
    }

    return `${numberOfCountries} countries`;
}

const getTrafficChangesEventTitle = (trafficChanges: TrafficChangesModel, domain: string): string => {
    const actionText = trafficChanges.direction;

    const countryText = buildCountryText(trafficChanges);
    const channelName = buildTrafficMetricText(trafficChanges);
    const periodText = buildPeriodText(trafficChanges);
    const trafficChangesValue = trafficSignalFiltersService.getChangeValue(trafficChanges);

    return `${countryText} ${domain}'s ${channelName.toLowerCase()} ${actionText} ${periodText} by ${trafficChangesValue}%+`.trim();
};

const getNewMarketEventTitle = (
    newMarkets: TrafficMarketModel,
    countries: TrafficSignalEventDto["countries"],
): string => {
    const trafficMetricText = buildTrafficMetricText(newMarkets);
    const thresholdValue = trafficSignalFiltersService.getThresholdValue(newMarkets);
    const countriesText = buildCountriesText(countries ?? []);

    return `New market: ${trafficMetricText} crossed ${thresholdValue} in ${countriesText}`;
};

export const getTrafficSignalEventTitle = (signal: TrafficSignalEventDto, domain: string): string => {
    const { filters, countries } = signal;

    if (isTrafficChangesModel(filters.trafficChanges)) {
        return getTrafficChangesEventTitle(filters.trafficChanges, domain);
    }

    if (isTrafficMarketModel(filters.newMarkets)) {
        return getNewMarketEventTitle(filters.newMarkets, countries);
    }

    return "";
};
